import { Link } from "react-router-dom";
import { ShieldCheck, Award, Users, MapPin, Phone, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/site/SEO";
import { COMPANY } from "@/lib/constants";

const stats = [
  { value: "2022", label: "Locally Established" },
  { value: "100%", label: "Recommended" },
  { value: "5★", label: "Customer Rating" },
  { value: "8+", label: "Cities Served" },
];

const values = [
  "Honest, upfront pricing with no surprises",
  "Proper base prep and reinforcement on every job",
  "Clean, respectful crews that protect your property",
  "Finishes engineered for Wisconsin freeze-thaw",
];

export default function About() {
  return (
    <div>
      <SEO title="About CG Concrete LLC | Watertown, WI Concrete Contractor" description="CG Concrete LLC is a locally owned concrete contractor in Watertown, WI, owned by Carlos Garcia. Licensed, insured, and committed to quality workmanship." />
      <section className="bg-secondary text-white grain-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-primary font-bold text-sm tracking-widest">ABOUT US</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-3">Your Local Concrete Crew</h1>
            <p className="text-white/80 mt-6 text-lg leading-relaxed">
              CG Concrete LLC is a locally owned and operated concrete contractor based in {COMPANY.city}, {COMPANY.state}. Founded in {COMPANY.founded} by {COMPANY.owner}, we've built our reputation on solid work, fair pricing, and treating every property like it's our own.
            </p>
            <p className="text-white/70 mt-4 leading-relaxed">
              From driveways and patios to garage floors and stamped concrete, we bring the same attention to detail to every pour — big or small.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild className="bg-primary hover:bg-primary/90 rounded-sm font-bold h-12 px-6"><Link to="/contact">Get a Free Quote</Link></Button>
              <Button asChild variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-secondary rounded-sm font-bold h-12 px-6"><a href={`tel:${COMPANY.phoneRaw}`}><Phone className="mr-2 h-4 w-4" />{COMPANY.phone}</a></Button>
            </div>
          </div>
          <div className="relative">
            <img src="/archive/project1.jpg" alt="CG Concrete company truck" className="w-full aspect-[4/5] object-cover border border-white/10" />
          </div>
        </div>
      </section>

      <section className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 divide-x divide-border">
          {stats.map((s) => (
            <div key={s.label} className="py-10 px-4 text-center" data-testid={`about-stat-${s.label}`}>
              <div className="font-heading text-4xl font-extrabold text-primary">{s.value}</div>
              <div className="text-sm text-muted-foreground mt-1 font-medium">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-heading text-3xl font-extrabold">What We Stand For</h2>
          <ul className="mt-6 space-y-4">
            {values.map((v) => (
              <li key={v} className="flex items-start gap-3"><Check className="h-5 w-5 text-primary mt-0.5 shrink-0" /><span className="font-medium">{v}</span></li>
            ))}
          </ul>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {[{ icon: ShieldCheck, t: "Licensed & Insured" }, { icon: Award, t: "Quality Guaranteed" }, { icon: Users, t: "Family Owned" }].map((b) => (
            <div key={b.t} className="bg-card border border-border p-6 text-center">
              <b.icon className="h-8 w-8 text-primary mx-auto" />
              <p className="font-bold mt-3 text-sm">{b.t}</p>
            </div>
          ))}
          <div className="sm:col-span-3 bg-secondary text-white p-6 flex items-center gap-3">
            <MapPin className="h-6 w-6 text-primary shrink-0" />
            <span className="font-medium">{COMPANY.address}, {COMPANY.city}, {COMPANY.state} {COMPANY.zip} · {COMPANY.hours}</span>
          </div>
        </div>
      </section>
    </div>
  );
}
